import { ContentCluster } from '@app/components/base/content-structure/ContentCluster';
import { SwitchCardField } from '@app/components/form/SwitchCardField';
import { ThumbFieldsMui } from '@app/components/form/ThumbFieldsMui';
import { SocialPublishAtField } from '@app/modules/social-export/components/inputs/SocialPublishAtField';
import { TextFieldElementWithEmoji } from '@app/modules/social-export/components/inputs/TextFieldElementWithEmoji';
import { PublishFormUnsavedChangesWarning } from '@app/modules/social-export/components/PublishFormUnsavedChangesWarning';
import { BaseExportFormProps } from '@app/modules/social-export/hooks/social-export-form-props';
import { publishMessages } from '@app/modules/social-export/publish';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import { useIntl } from 'react-intl';
import { InstagramPublishDto, InstagramSettingsDto } from '../../social-export.types';
import { InstagramPublishValues, useInstagramPublishSchema } from './useInstagramPublishSchema';

type InstagramPublishFormProps = BaseExportFormProps<InstagramPublishDto, InstagramSettingsDto>;

export function InstagramPublishForm({
  source,
  onUpload,
  defaultSettings,
  videoUrl,
  actions,
}: InstagramPublishFormProps) {
  const { $t } = useIntl();
  const schema = useInstagramPublishSchema();
  const initialValues: InstagramPublishValues = {
    content: source.description ?? '',
    shareToFeed: defaultSettings?.shareToFeed ?? true,
    userTags: '',
    thumbType: 'DEFAULT',
    coverUrl: null,
    thumbOffsetMs: null,
    publishAt: null,
  };

  const beforeSubmit = (data: InstagramPublishValues): InstagramPublishDto => ({
    description: data.content,
    thumbOffsetMs: data.thumbType === 'VIDEO_FRAME' ? (data.thumbOffsetMs ?? undefined) : undefined,
    coverUrl: data.thumbType === 'IMAGE_URL' ? (data.coverUrl ?? undefined) : undefined,
    shareToFeed: data.shareToFeed,
    userTags: data.userTags
      ? data.userTags
          .split(',')
          .map((s) => s.trim().replace('@', ''))
          .filter(Boolean)
      : undefined,
    publishAt: data.publishAt || undefined,
  });

  return (
    <FormContainer
      values={initialValues}
      mode="onBlur"
      resolver={zodResolver(schema)}
      onSuccess={async (formData) => {
        await onUpload(beforeSubmit(formData));
      }}
    >
      <PublishFormUnsavedChangesWarning />
      <ContentCluster
        title={$t({
          id: 'socialExport.form.content',
          defaultMessage: 'Contenido',
        })}
      >
        <Stack spacing={3}>
          <TextFieldElementWithEmoji
            name="content"
            label={$t({
              id: 'socialExport.form.content',
              defaultMessage: 'Contenido',
            })}
            required
            fullWidth
            multiline
            rows={3}
          />
          <SwitchCardField
            name="shareToFeed"
            label={$t({
              id: 'socialExport.form.publishToFeed',
              defaultMessage: 'Publicar también en el feed',
            })}
            description={$t(publishMessages.shareToFeedDescription)}
          />
          <TextFieldElement
            name="userTags"
            label={$t({
              id: 'socialExport.form.taggedUsers',
              defaultMessage: 'Usuarios etiquetados',
            })}
            helperText={$t({
              id: 'socialExport.form.idsSeparatedByComma',
              defaultMessage: 'Ids separados por coma',
            })}
            fullWidth
          />
        </Stack>
      </ContentCluster>
      <ContentCluster
        title={$t({
          id: 'socialExport.form.thumbnail',
          defaultMessage: 'Thumbnail',
        })}
      >
        <ThumbFieldsMui urlFieldName="coverUrl" maxSizeMb={8} videoUrl={videoUrl} />
      </ContentCluster>
      <ContentCluster
        title={$t({
          id: 'socialExport.form.scheduling',
          defaultMessage: 'Programación',
        })}
      >
        <SocialPublishAtField />
      </ContentCluster>
      {actions}
    </FormContainer>
  );
}
